/**
 * 1 Nov 2026 (D-1102-CREW-2): Today reads again every minute while it is open AND showing, so a move the office makes
 * is on the crew's phone within a minute. A hidden page does not read: the timer is stopped when the page is hidden,
 * and a read on return is already there (visibilitychange / pageshow in Today).
 */
import { useEffect, useRef } from 'react';
import type { TodayVisit } from '@shared/api-types';
import { LIVE_REFRESH_MS } from '@shared/crew-1102';

const visible = () => typeof document === 'undefined' || document.visibilityState === 'visible';
const online = () => typeof navigator === 'undefined' || navigator.onLine !== false;

/**
 * Calls `tick` every `ms` while the page is visible and the phone is online. Nothing runs while it is hidden (the
 * interval is cleared, not merely ignored) and it starts again, without an extra call, when the page shows again.
 */
export function useVisibleInterval(tick: () => void, enabled: boolean, ms = LIVE_REFRESH_MS): void {
  const latest = useRef(tick);
  latest.current = tick;
  useEffect(() => {
    if (!enabled) return;
    let handle: ReturnType<typeof setInterval> | null = null;
    const stop = () => {
      if (handle !== null) clearInterval(handle);
      handle = null;
    };
    const start = () => {
      if (handle !== null || !visible()) return;
      handle = setInterval(() => {
        if (visible() && online()) latest.current();
      }, ms);
    };
    const onVisibility = () => (visible() ? start() : stop());
    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, ms]);
}

/** What makes a card "changed": where it sits in the day and the time it was given — not what the crew pressed. */
export function placeOf(v: Pick<TodayVisit, 'position' | 'chosen' | 'timeLabel'>): string {
  return [v.position ?? '', v.chosen ? `${v.chosen.start}${v.chosen.booked ? 'b' : ''}` : '', v.timeLabel ?? ''].join('|');
}

/**
 * The visits whose place or time changed between two reads of the same day, and the visits that came or went
 * (a visit moved off the day, or onto it). `ids` are the ones still on the day, to be marked on their cards;
 * `count` is how many changed in all, for the one announcement.
 */
export function changedBetween(before: readonly TodayVisit[], after: readonly TodayVisit[]): { ids: string[]; count: number } {
  const was = new Map(before.map((v) => [v.appointmentId, placeOf(v)]));
  const ids: string[] = [];
  let count = 0;
  for (const v of after) {
    const prev = was.get(v.appointmentId);
    if (prev === undefined || prev !== placeOf(v)) {
      ids.push(v.appointmentId);
      count++;
    }
  }
  const now = new Set(after.map((v) => v.appointmentId));
  for (const id of was.keys()) if (!now.has(id)) count++;
  return { ids, count };
}

/** How long a changed card stays marked. */
export const CHANGED_MARK_MS = 8_000;
